import { useState, useEffect } from "react";
import {
  Form,
  Input,
  InputNumber,
  DatePicker,
  Card,
  Row,
  Col,
  Button,
  Divider,
  Typography,
  Space,
  Select,
  message,
  Empty,
} from "antd";
import {
  FileTextOutlined,
  DollarOutlined,
  BankOutlined,
  CalendarOutlined,
  SaveOutlined,
  ClearOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import TextArea from "antd/es/input/TextArea";
import { useSelector } from "react-redux";
import {
  getAllParticipanteByid,
  postParticipanteFormato,
} from "../../../../services/paati/actasFormatos/paatiFormatos";

const { Title, Text } = Typography;

// ─── Cabecera institucional ────────────────────────────────────────────────────
function HeaderInstitucional() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        border: "1.5px solid #1a2535",
        borderRadius: 8,
        overflow: "hidden",
        marginBottom: 28,
        background: "#fff",
        boxShadow: "0 2px 10px rgba(53,95,145,0.10)",
      }}
    >
      {/* Logo */}
      <div
        style={{
          width: 130,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRight: "1.5px solid #1a2535",
          padding: "10px 14px",
          background: "#e8f0f9",
        }}
      >
        <Text
          strong
          style={{ color: "#355F91", fontSize: 13, textAlign: "center" }}
        >
          ARN
        </Text>
      </div>

      {/* Título central */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRight: "1.5px solid #1a2535",
          padding: "10px 20px",
        }}
      >
        <Title
          level={5}
          style={{
            margin: 0,
            textAlign: "center",
            color: "#1a2535",
            fontSize: 13,
          }}
        >
          ACTA DE FINALIZACIÓN DE ASISTENCIA TÉCNICA A PROYECTOS PRODUCTIVOS EN
          REINCORPORACIÓN
        </Title>
      </div>

      {/* Código / Fecha / Versión */}
      <div style={{ width: 160, display: "flex", flexDirection: "column" }}>
        <div
          style={{
            borderBottom: "1px solid #1a2535",
            padding: "6px 10px",
            display: "flex",
            gap: 4,
          }}
        >
          <Text strong style={{ fontSize: 10 }}>
            CÓDIGO:
          </Text>
          <Text style={{ fontSize: 10 }}>IR-F-125</Text>
        </div>
        <div style={{ display: "flex", flex: 1 }}>
          <div
            style={{
              flex: 1,
              borderRight: "1px solid #1a2535",
              padding: "6px 8px",
            }}
          >
            <div>
              <Text strong style={{ fontSize: 9 }}>
                FECHA:
              </Text>
            </div>
            <Text style={{ fontSize: 9 }}>2021-08-10</Text>
          </div>
          <div style={{ flex: 1, padding: "6px 8px" }}>
            <div>
              <Text strong style={{ fontSize: 9 }}>
                VERSIÓN:
              </Text>
            </div>
            <Text style={{ fontSize: 9 }}>V-1</Text>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tarjeta de sección ────────────────────────────────────────────────────────
function SectionCard({ icon, number, title, children }) {
  return (
    <Card
      style={{
        marginBottom: 20,
        borderRadius: 10,
        border: "1.5px solid #c8d8ec",
        boxShadow: "0 2px 8px rgba(53,95,145,0.06)",
      }}
      styles={{ body: { padding: "20px 24px" } }}
      title={
        <Space>
          <div
            style={{
              background: "#355F91",
              color: "#fff",
              borderRadius: "50%",
              width: 26,
              height: 26,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {number}
          </div>
          {icon}
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#1a2535",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {title}
          </span>
        </Space>
      }
      headStyle={{
        background: "linear-gradient(135deg, #e8f0f9 0%, #f4f7fb 100%)",
        borderBottom: "1.5px solid #c8d8ec",
        minHeight: 48,
      }}
    >
      {children}
    </Card>
  );
}

// ─── Formulario principal ──────────────────────────────────────────────────────
export default function FormActaFinalizacion({
  proyectoId,
  tipoFormatoID,
  onClose,
  updateDataLocal,
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const usernameFromState = useSelector((state) => state.auth.user);

  const meses = [
    { value: 1, label: "Enero" },
    { value: 2, label: "Febrero" },
    { value: 3, label: "Marzo" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Mayo" },
    { value: 6, label: "Junio" },
    { value: 7, label: "Julio" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Septiembre" },
    { value: 10, label: "Octubre" },
    { value: 11, label: "Noviembre" },
    { value: 12, label: "Diciembre" },
  ];

  useEffect(() => {
    // 🔥 No ejecutar si aún no existen los valores
    if (!proyectoId || !tipoFormatoID) return;

    const fetchProyecto = async () => {
      try {
        setLoading(true);
        const response = await getAllParticipanteByid(
          proyectoId,
          tipoFormatoID,
        );
        if (response?.success) {
          const data = response.data || {};
          if (data.isEditable) {
            form.setFieldsValue({
              ...data,
            });
          } else {
            const hoy = dayjs();
            const mesTexto = hoy.format("MMMM");
            const mesCapitalizado =
              mesTexto.charAt(0).toUpperCase() + mesTexto.slice(1);

            form.setFieldsValue({
              ...data,
              // 🔥 Fecha actual para firma
              dia_firma: hoy.date(),
              mes_firma: mesCapitalizado,
              vigencia_firma: hoy.year(),
              lista_participantes: data.lista_participantes,
              total_participantes: data.lista_participantes.length,
              nombre_organizacion:
                "Organización de las Naciones Unidas para la Alimentación y la Agricultura_FAO_Colombia",
              valor_total_proyecto:
                Number(data.valor_arn || 0) + Number(data.valor_fao || 0),
              valor_apoyo_economico_reincorporacion: Number(
                data.valor_arn || 0,
              ),
              valor_otros_aportes: Number(data.valor_fao || 0),
              valor_aportes_propios: 0,
              organizacion:
                data?.tipo_forma === "Asociativa"
                  ? data?.lista_participantes
                      ?.map((item) => item?.codigo_sirr)
                      .filter(Boolean)
                      .join(", ")
                  : "No aplica",
            });
          }

          message.success("Datos cargados correctamente");
        } else {
          form.setFieldsValue({});
          onClose();
          message.error("Ocurrió un error al obtener los datos");
        }
      } catch (error) {
        console.error("Error cargando proyecto:", error);
        form.setFieldsValue({});
        onClose();
        message.error("Ocurrió un error al obtener los datos");
      } finally {
        setLoading(false);
      }
    };

    fetchProyecto();
  }, [proyectoId, tipoFormatoID]);

  // Extrae dia/mes/vigencia del DatePicker
  const buildPayload = (values) => ({
    ...values,
  });

  // Submit final (botón Guardar)
  // Submit final (botón Guardar)
  const handleFinish = async (values) => {
    try {
      const payload = buildPayload(values);

      const data = {
        oidTipoFormato: tipoFormatoID,
        data: payload,
        usuario: usernameFromState.username,
      };

      const response = await postParticipanteFormato(proyectoId, data);

      if (response?.success) {
        message.success(response.message || "Guardado correctamente");
        updateDataLocal(values);
        onClose();
      } else {
        message.error(response?.message || "Ocurrió un error al guardar");
      }
    } catch (error) {
      message.error("Error al cargar datos");
    }
  };

  const inputNumberProps = {
    style: { width: "100%" },
    formatter: (v) => (v ? `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".") : ""),
    parser: (v) => (v ? v.replace(/\$\s?|(\.*)/g, "") : ""),
    min: 0,
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(160deg, #e8f0f9 0%, #f4f7fb 60%, #eaf3ef 100%)",
        padding: "32px 16px",
        fontFamily: "'Segoe UI', sans-serif",
      }}
    >
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <HeaderInstitucional />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          requiredMark="optional"
          size="middle"
        >
          {/* ── Fecha, ciudad y código ─────────────────────────── */}
          <Card
            style={{
              marginBottom: 20,
              borderRadius: 10,
              border: "1.5px solid #c8d8ec",
              boxShadow: "0 2px 8px rgba(53,95,145,0.06)",
            }}
            styles={{ body: { padding: "16px 24px" } }}
          >
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Código  SIRR"
                  name="codigo_sir"
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Input disabled placeholder="Código sir del participante" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* ── Sección 1: Identificación del proyecto ─────────── */}
          <SectionCard
            number="1"
            icon={<FileTextOutlined style={{ color: "#355F91" }} />}
            title="Identificación del Proyecto Productivo"
          >
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Nombre del proyecto productivo"
                  name="nombre_proyecto"
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Input disabled placeholder="Nombre completo del proyecto" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Nombre de la forma Asociativa"
                  name="organizacion"
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Input disabled placeholder="" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Dependencia de la ARN (Grupo Territorial)"
                  name="grupo_territorial"
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Input disabled placeholder="Nombre del grupo territorial" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Código del proyecto PRE-PP"
                  name="codigo_pre"
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Input disabled placeholder="Código PRE-PP" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="N.º personas en reincorporación vinculadas"
                  name="total_participantes"
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <InputNumber
                    disabled
                    style={{ width: "100%" }}
                    min={1}
                    placeholder="0"
                  />
                </Form.Item>
              </Col>
            </Row>
          </SectionCard>

          {/* ── Sección 2: Ubicación del proyecto ──────────────── */}

          <SectionCard
            number="2"
            icon={<DollarOutlined style={{ color: "#355F91" }} />}
            title=" Ubicación del proyecto"
          >
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Departamento"
                  name="departamento"
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Input disabled placeholder="Departamento del proyecto" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Municipio"
                  name="municipio"
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Input disabled placeholder="Municipio del proyecto" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Vereda / Dirección"
                  name="ubicacion_proyecto"
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Input placeholder="Vereda o dirección del proyecto" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Acompañamiento  y asistencia técnica por"
                  name="nombre_organizacion"
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <TextArea disabled placeholder="Nombre de la organización" />
                </Form.Item>
              </Col>
            </Row>
          </SectionCard>

          {/* ── Sección 3: Información financiera ──────────────── */}
          <SectionCard
            number="3"
            icon={<DollarOutlined style={{ color: "#355F91" }} />}
            title="Información Financiera del Proyecto Productivo"
          >
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Valor del total del proyecto"
                  name="valor_total_proyecto"
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <InputNumber
                    disabled
                    {...inputNumberProps}
                    placeholder="$ 0"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Valor del apoyo económico de reincorporación"
                  name="valor_apoyo_economico_reincorporacion"
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <InputNumber
                    disabled
                    {...inputNumberProps}
                    placeholder="$ 0"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Valor de los aportes propios"
                  name="valor_aportes_propios"
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <InputNumber
                    disabled
                    {...inputNumberProps}
                    placeholder="$ 0"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8}>
                <Form.Item
                  label="Valor otros aportes"
                  name="valor_otros_aportes"
                >
                  <InputNumber
                    disabled
                    {...inputNumberProps}
                    placeholder="$ 0"
                  />
                </Form.Item>
              </Col>
            </Row>
          </SectionCard>

          {/* ── Sección 4: Personas vinculadas al proyecto ────────────── */}
          <SectionCard
            number="4"
            icon={<TeamOutlined style={{ color: "#355F91" }} />}
            title="Personas vinculadas al proyecto"
          >
            {/* 🔹 Campo oculto para guardar en el form */}
            <Form.Item name="lista_participantes" hidden>
              <Input />
            </Form.Item>

            <Form.Item
              shouldUpdate={(prev, curr) =>
                prev.lista_participantes !== curr.lista_participantes
              }
              noStyle
            >
              {({ getFieldValue }) => {
                const participantes =
                  getFieldValue("lista_participantes") || [];

                if (!participantes.length) {
                  return (
                    <Empty description="No hay participantes disponibles" />
                  );
                }

                return (
                  <Row gutter={[12, 12]}>
                    {participantes.map((p, index) => (
                      <Col xs={24} key={p.participante_id}>
                        <Card size="small" bordered>
                          <Row gutter={[8, 4]}>
                            <Col span={24}>
                              <b>
                                {index + 1}. {p.nombre_completo}
                              </b>
                            </Col>
                            <Col xs={24} sm={8}>
                              <Text type="secondary">Identificación:</Text>
                              <br />
                              {p.identificacion}
                            </Col>
                            <Col xs={24} sm={8}>
                              <Text type="secondary">Código SIRR:</Text>
                              <br />
                              {p.codigo_sirr}
                            </Col>
                          </Row>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                );
              }}
            </Form.Item>
          </SectionCard>

          {/* ── Sección 5: FIRMA DEL ACTA DE INICIO──────────────── */}
          <SectionCard
            number="5"
            icon={<FileTextOutlined style={{ color: "#355F91" }} />}
            title="Firma del acta de inicio"
          >
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={5}>
                <Form.Item
                  label="Selecciona el día"
                  name="dia_firma"
                  rules={[{ required: true, message: "Seleciona un día" }]}
                >
                  <Select placeholder="Día">
                    {Array.from({ length: 31 }, (_, i) => (
                      <Select.Option key={i + 1} value={i + 1}>
                        {i + 1}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={5}>
                <Form.Item
                  label="Selecciona el mes"
                  name="mes_firma"
                  rules={[{ required: true, message: "Seleciona un mes" }]}
                >
                  <Select placeholder="Mes">
                    {meses.map((mes) => (
                      <Select.Option key={mes.value} value={mes.label}>
                        {mes.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={5}>
                <Form.Item
                  label="Selecciona el año"
                  name="vigencia_firma"
                  rules={[{ required: true, message: "Selecciona un año" }]}
                >
                  <Select placeholder="Año">
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = 2026 + i;
                      return (
                        <Select.Option key={year} value={year}>
                          {year}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={9}>
                <Form.Item
                  label="Escribe la ciudad , departamento"
                  name="ciudad_firma"
                >
                  <TextArea />
                </Form.Item>
              </Col>
            </Row>
          </SectionCard>

          {/* ── Botones ──────────────────────────────────────────── */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
              paddingBottom: 32,
            }}
          >
            <Button
              icon={<ClearOutlined />}
              onClick={() => form.resetFields()}
              size="large"
              style={{ borderRadius: 8 }}
            >
              Limpiar
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              size="large"
              style={{
                background: "#355F91",
                borderColor: "#355F91",
                borderRadius: 8,
                fontWeight: 600,
                paddingInline: 32,
              }}
            >
              Guardar acta
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
